import React, { useEffect, useState } from 'react';
import { supabase } from '../../../lib/supabase';
import ReactDOM from 'react-dom';
import Icon from 'components/AppIcon';
import { formatMoney } from '../../../utils/formatMoney';

const PAYMENT_LABELS = {
  cash: 'Efectivo',
  bank_transfer: 'Transferencia',
  card: 'Tarjeta',
  check: 'Cheque',
  other: 'Otro',
  credit: 'Cuenta corriente',
  efectivo: 'Efectivo',
  transferencia: 'Transferencia',
  tarjeta: 'Tarjeta',
  otro: 'Otro',
};

function pad(n) { return String(n).padStart(4, '0'); }

function formatDateTime(dt) {
  const d = dt ? new Date(dt) : new Date();
  return d.toLocaleString('es-CL', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

const formatCurrency = (n, currency) => formatMoney(n, currency);

function Divider() {
  return <div style={{ borderTop: '1px dashed #999', margin: '6px 0' }} />;
}

function Row({ label, value, bold }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, fontWeight: bold ? 700 : 400, margin: '2px 0' }}>
      <span>{label}</span>
      <span>{value}</span>
    </div>
  );
}

export default function CrmThermalTicket({
  business,
  sale,
  items = [],
  customer,
  paymentMethod = 'efectivo',
  payments = [],
  discountAmount = 0,
  subtotal = 0,
  total = 0,
  amountReceived = null,
  change = null,
  initialPaymentAmount = null,
  initialPaymentMethod = null,
  pendingBalance = null,
  notes,
  createdAt,
  onNewSale,
  onClose,
  onReprint,
  // PRINT-2 — estado de la impresión real vía PrintService/QZ Tray.
  // Opcionales y con default 'idle' para no romper ningún caller que no
  // los pase: sin esto, el modal se comporta exactamente igual que antes.
  printStatus = 'idle',
  printErrorMessage = null,
  onConfigurePrinter,
}) {
  // Pilot-only fiscal status. This never issues a DTE or allocates a folio.
  const fiscalPilotBusinessId = 'cda904a1-e331-4bd4-9f33-a6715687d3dd';
  const showFiscalPilot = business?.id === fiscalPilotBusinessId;
  const [fiscalCheck, setFiscalCheck] = useState({ status: 'idle', data: null });
  useEffect(() => {
    if (!showFiscalPilot || !sale?.id) return;
    let active = true;
    setFiscalCheck({ status: 'checking', data: null, error: null });
    (async () => {
      try {
        const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
        if (sessionError || !sessionData?.session?.access_token) throw new Error('SESSION_UNAVAILABLE');
        const response = await fetch('/api/sii/boleta-readiness', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${sessionData.session.access_token}` },
          body: JSON.stringify({ businessId: business.id, saleId: sale.id }),
        });
        const contentType = response.headers.get('content-type') || '';
        if (!contentType.toLowerCase().includes('application/json')) {
          throw new Error(`FISCAL_API_NON_JSON_HTTP_${response.status}`);
        }
        const data = await response.json().catch(() => { throw new Error(`FISCAL_API_INVALID_JSON_HTTP_${response.status}`); });
        if (!response.ok) throw new Error(data?.error || `HTTP_${response.status}`);
        if (active) setFiscalCheck({ status: 'ready', data, error: null });
      } catch (error) {
        if (active) setFiscalCheck({ status: 'error', data: null, error: String(error?.message || 'NETWORK_ERROR').slice(0, 100) });
      }
    })();
    return () => { active = false; };
  }, [showFiscalPilot, business?.id, sale?.id]);

  const [fiscalPreflight, setFiscalPreflight] = useState({ status: 'idle', data: null, error: null });
  useEffect(() => {
    if (!showFiscalPilot || !sale?.id) return;
    let active = true;
    setFiscalPreflight({ status: 'checking', data: null, error: null });
    (async () => {
      try {
        const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
        if (sessionError || !sessionData?.session?.access_token) throw new Error('SESSION_UNAVAILABLE');
        const response = await fetch('/api/sii/boleta-preflight', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${sessionData.session.access_token}` },
          body: JSON.stringify({ businessId: business.id, saleId: sale.id }),
        });
        if (!(response.headers.get('content-type') || '').includes('application/json'))
          throw new Error(`FISCAL_PREFLIGHT_NON_JSON_${response.status}`);
        const data = await response.json();
        if (!response.ok) throw new Error(data?.error || `HTTP_${response.status}`);
        if (active) setFiscalPreflight({ status: 'ready', data, error: null });
      } catch (error) {
        if (active) setFiscalPreflight({ status: 'error', data: null, error: String(error?.message || 'NETWORK_ERROR').slice(0, 100) });
      }
    })();
    return () => { active = false; };
  }, [showFiscalPilot, business?.id, sale?.id]);

  const [taxSelection, setTaxSelection] = useState({});
  const [taxEvidence, setTaxEvidence] = useState({});
  const [taxSaving, setTaxSaving] = useState({});
  const saveTaxClassification = async (itemId) => {
    if (!showFiscalPilot || !sale?.id || taxSaving[itemId] === 'saving') return;
    setTaxSaving(previous => ({...previous, [itemId]: 'saving'}));
    try {
      const {data:sessionData,error:sessionError}=await supabase.auth.getSession();
      if(sessionError || !sessionData?.session?.access_token)throw new Error('SESSION_UNAVAILABLE');
      const response=await fetch('/api/sii/boleta-verify-tax',{
        method:'POST',
        headers:{'Content-Type':'application/json',Authorization:`Bearer ${sessionData.session.access_token}`},
        body:JSON.stringify({businessId:business.id,saleId:sale.id,itemId,
          category:taxSelection[itemId],evidence:taxEvidence[itemId]})
      });
      if(!(response.headers.get('content-type')||'').includes('application/json'))throw new Error('INVALID_API_RESPONSE');
      const result=await response.json();
      if(!response.ok || result.verified!==true)throw new Error(result.error||'VERIFICATION_FAILED');
      setTaxSaving(previous=>({...previous,[itemId]:'saved'}));
      const check=await fetch('/api/sii/boleta-preflight',{
        method:'POST',headers:{'Content-Type':'application/json',Authorization:`Bearer ${sessionData.session.access_token}`},
        body:JSON.stringify({businessId:business.id,saleId:sale.id})
      });
      if(check.ok && (check.headers.get('content-type')||'').includes('application/json')){
        const data=await check.json();
        setFiscalPreflight({status:'ready',data,error:null});
      }
    }catch(error){setTaxSaving(previous=>({...previous,[itemId]:String(error?.message||'ERROR').slice(0,90)}));}
  };

  const [fiscalPreparation, setFiscalPreparation] = useState({ status: 'idle', error: null });
  const prepareFiscalOperation = async () => {
    if (!showFiscalPilot || !sale?.id || fiscalPreparation.status === 'working') return;
    setFiscalPreparation({ status: 'working', error: null });
    try {
      const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
      if (sessionError || !sessionData?.session?.access_token) throw new Error('SESSION_UNAVAILABLE');
      const response = await fetch('/api/sii/boleta-prepare', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${sessionData.session.access_token}` },
        body: JSON.stringify({ businessId: business.id, saleId: sale.id }),
      });
      if (!(response.headers.get('content-type') || '').includes('application/json')) throw new Error(`FISCAL_API_HTTP_${response.status}`);
      const data = await response.json();
      if (!response.ok || data?.message !== 'OPERATION_PREPARED_NO_DTE_SENT') throw new Error(data?.error || `HTTP_${response.status}`);
      setFiscalPreparation({ status: 'prepared', error: null });
      setFiscalCheck((previous) => ({ ...previous, data: { ...previous.data, fiscalOperationStatus: data.operationStatus } }));
    } catch (error) {
      setFiscalPreparation({ status: 'error', error: String(error?.message || 'UNKNOWN_ERROR').slice(0, 100) });
    }
  };

  // Inject print styles while modal is open.
  // The print div is portaled directly into document.body so
  // `body > *:not(#crm-thermal-print-root)` can hide the entire app
  // (including the modal) while leaving only the ticket visible — 1 page.
  useEffect(() => {
    const styleId = 'crm-thermal-print-style';
    const style = document.createElement('style');
    style.id = styleId;
    style.textContent = `
      @media print {
        /* Hide the entire React app tree and everything else in body */
        body > *:not(#crm-thermal-print-root) {
          display: none !important;
        }

        /* Show the ticket portal */
        #crm-thermal-print-root {
          display: block !important;
          position: static !important;
          width: 58mm !important;
          max-width: 58mm !important;
          margin: 0 !important;
          padding: 0 !important;
          background: #fff !important;
          color: #000 !important;
          overflow: visible !important;
        }

        #crm-thermal-print-root *:not(img) {
          color: #000 !important;
          background: transparent !important;
          box-shadow: none !important;
        }

        html, body {
          margin: 0 !important;
          padding: 0 !important;
          background: #fff !important;
        }

        @page {
          size: 58mm auto;
          margin: 0;
        }
      }
    `;
    document.head.appendChild(style);
    return () => {
      const el = document.getElementById(styleId);
      if (el) el.remove();
    };
  }, []);

  const ticketNumber = sale?.invoice_number
    ? `NV-${pad(sale.invoice_number)}`
    : `T-${Date.now().toString().slice(-6)}`;

  const customerName = customer?.name || 'Consumidor final';

  const logoUrl = business?.logoUrl || business?.logo_url || null;
  const address = business?.address || null;
  const phone = business?.whatsapp || null;
  const normalizedPayments = (payments || [])
    .map((payment) => ({
      method: payment.payment_method || payment.method,
      amount: Number(payment.amount || 0),
    }))
    .filter((payment) => payment.amount > 0);
  const paidAmount = normalizedPayments.length > 0
    ? normalizedPayments.reduce((sum, payment) => sum + payment.amount, 0)
    : paymentMethod === 'credit'
      ? Number(initialPaymentAmount || 0)
      : Number(total || 0);
  const pendingAmount = pendingBalance != null
    ? Number(pendingBalance || 0)
    : Math.max(0, Number(total || 0) - paidAmount);
  const paymentState = pendingAmount <= 0
    ? 'Pagada'
    : paidAmount > 0
      ? 'Parcial'
      : 'Pendiente';

  // Ticket content — rendered in the modal preview AND in the print portal.
  // The modal copy is for screen preview only (hidden during print).
  // The portal copy is the sole source of print output.
  const ticketBody = (
    <div style={{
      padding: '16px 14px',
      fontFamily: "'Courier New', Courier, monospace",
      fontSize: 11,
      color: '#111',
      lineHeight: 1.4,
      backgroundColor: '#fff',
      width: '100%',
    }}>
      {/* Header */}
      <div style={{ textAlign: 'center', marginBottom: 8 }}>
        {logoUrl && (
          <div style={{ marginBottom: 6, textAlign: 'center' }}>
            <img
              src={logoUrl}
              alt={business?.name || 'Logo'}
              crossOrigin="anonymous"
              style={{ maxWidth: 260, maxHeight: 120, width: '90%', objectFit: 'contain', display: 'inline-block' }}
            />
          </div>
        )}
        <div style={{ fontWeight: 700, fontSize: 14, letterSpacing: 1, textTransform: 'uppercase' }}>
          {business?.name || 'Mi Negocio'}
        </div>
        {address && (
          <div style={{ fontSize: 10, color: '#555', marginTop: 2 }}>{address}</div>
        )}
        {phone && (
          <div style={{ fontSize: 10, color: '#555', marginTop: 1 }}>{phone}</div>
        )}
        <div style={{ fontSize: 10, color: '#555', marginTop: 2 }}>Terminal de Ventas</div>
      </div>

      <Divider />

      <div style={{ fontSize: 10, color: '#444', marginBottom: 4 }}>
        <div>Ticket: <strong>{ticketNumber}</strong></div>
        <div>Fecha: {formatDateTime(createdAt)}</div>
        <div>Cliente: {customerName}</div>
      </div>

      <Divider />

      {/* Items */}
      <div style={{ marginBottom: 4 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, fontWeight: 700, marginBottom: 4, color: '#555' }}>
          <span style={{ flex: '0 0 28px' }}>Cant</span>
          <span style={{ flex: 1 }}>Producto</span>
          <span style={{ flex: '0 0 64px', textAlign: 'right' }}>Total</span>
        </div>
        {items.map((item, i) => (
          <div key={item._key || item.product_id || item.id || i} style={{ marginBottom: 3 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11 }}>
              <span style={{ flex: '0 0 28px', color: '#555' }}>{item.quantity}x</span>
              <span style={{ flex: 1, fontWeight: 600, wordBreak: 'break-word', paddingRight: 4 }}>{item.name}</span>
              <span style={{ flex: '0 0 64px', textAlign: 'right' }}>
                {formatCurrency(item.unit_price * item.quantity, business?.currency)}
              </span>
            </div>
            {item.quantity > 1 && (
              <div style={{ paddingLeft: 28, fontSize: 10, color: '#888' }}>
                {formatCurrency(item.unit_price, business?.currency)} c/u
              </div>
            )}
            {item.note && (
              <div style={{ paddingLeft: 28, fontSize: 10, color: '#888', fontStyle: 'italic' }}>{item.note}</div>
            )}
          </div>
        ))}
      </div>

      <Divider />

      {/* Totals */}
      <div style={{ marginBottom: 4 }}>
        <Row label="Subtotal" value={formatCurrency(subtotal, business?.currency)} />
        {discountAmount > 0 && (
          <Row label="Descuento" value={`-${formatCurrency(discountAmount, business?.currency)}`} />
        )}
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 14, fontWeight: 700, margin: '6px 0' }}>
        <span>TOTAL</span>
        <span>{formatCurrency(total, business?.currency)}</span>
      </div>

      <Divider />

      <Row label="Estado" value={paymentState} bold />
      {normalizedPayments.length > 0 && (
        <>
          <div style={{ fontSize: 10, fontWeight: 700, marginTop: 4 }}>Pagos:</div>
          {normalizedPayments.map((payment, index) => (
            <Row
              key={`${payment.method}-${index}`}
              label={`- ${PAYMENT_LABELS[payment.method] || payment.method}`}
              value={formatCurrency(payment.amount, business?.currency)}
            />
          ))}
        </>
      )}
      <Row label="Pagado" value={formatCurrency(paidAmount, business?.currency)} />
      <Row label="Pendiente" value={formatCurrency(pendingAmount, business?.currency)} />
      {normalizedPayments.length === 0 && (
        <Row label="Forma de pago" value={PAYMENT_LABELS[paymentMethod] || paymentMethod} />
      )}
      {normalizedPayments.length === 0 && paymentMethod === 'credit' ? (
        <>
          {initialPaymentAmount != null && initialPaymentAmount > 0 && (
            <>
              <Row label="Metodo abono" value={PAYMENT_LABELS[initialPaymentMethod] || initialPaymentMethod || 'No informado'} />
              <Row label="Abono" value={formatCurrency(initialPaymentAmount, business?.currency)} bold />
            </>
          )}
        </>
      ) : normalizedPayments.length === 0 ? (
        <>
          {amountReceived != null && amountReceived > 0 && (
            <Row label="Pago recibido" value={formatCurrency(amountReceived, business?.currency)} />
          )}
          {change != null && change > 0 && (
            <Row label="Vuelto" value={formatCurrency(change, business?.currency)} bold />
          )}
        </>
      ) : (
        <>
          {change != null && change > 0 && (
            <Row label="Vuelto" value={formatCurrency(change, business?.currency)} bold />
          )}
        </>
      )}

      {notes && (
        <>
          <Divider />
          <div style={{ fontSize: 10, color: '#555' }}>
            <div style={{ fontWeight: 600, marginBottom: 2 }}>Notas:</div>
            <div>{notes}</div>
          </div>
        </>
      )}

      <Divider />

      <div style={{ textAlign: 'center', fontSize: 11, color: '#555', marginTop: 8, marginBottom: 4 }}>
        Gracias por su compra
      </div>
    </div>
  );

  return (
    <>
      {/* Modal overlay — screen only. Hidden entirely during @media print via
          `body > *:not(#crm-thermal-print-root) { display: none }`.            */}
      <div
        style={{
          position: 'fixed', inset: 0, zIndex: 9999,
          backgroundColor: 'rgba(0,0,0,0.55)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: 16,
        }}
        role="dialog"
        aria-modal="true"
        aria-label="Ticket de venta"
      >
        <div style={{
          backgroundColor: '#fff',
          borderRadius: 12,
          boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
          width: '100%',
          maxWidth: 400,
          maxHeight: '90vh',
          overflowY: 'auto',
          display: 'flex',
          flexDirection: 'column',
        }}>

          {/* Modal header */}
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '14px 16px', borderBottom: '1px solid #f0f0f0',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <div style={{
                width: 32, height: 32, borderRadius: 8, backgroundColor: '#d1fae5',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <Icon name="CheckCircle2" size={18} color="#059669" />
              </div>
              <div>
                <p style={{ fontWeight: 700, fontSize: 13, color: '#111827', margin: 0 }}>¡Venta registrada!</p>
                <p style={{ fontSize: 11, color: '#6b7280', margin: 0 }}>{ticketNumber}</p>
              </div>
            </div>
            <button
              onClick={onClose}
              style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4, borderRadius: 6, color: '#9ca3af' }}
              aria-label="Cerrar"
            >
              <Icon name="X" size={18} color="currentColor" />
            </button>
          </div>

          {/* PRINT-2 — estado de la impresión real (QZ Tray), no del navegador */}
          {printStatus === 'printing' && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 16px', backgroundColor: '#eff6ff', color: '#1d4ed8', fontSize: 12 }}>
              <Icon name="Loader2" size={14} color="currentColor" className="animate-spin" />
              Imprimiendo ticket…
            </div>
          )}
          {printStatus === 'success' && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 16px', backgroundColor: '#ecfdf5', color: '#059669', fontSize: 12 }}>
              <Icon name="CheckCircle2" size={14} color="currentColor" />
              Ticket impreso correctamente.
            </div>
          )}
          {printStatus === 'missing_printer' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: '10px 16px', backgroundColor: '#fffbeb', color: '#92400e', fontSize: 12 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Icon name="AlertTriangle" size={14} color="currentColor" />
                No hay una impresora configurada en este equipo.
              </div>
              {onConfigurePrinter && (
                <button
                  onClick={onConfigurePrinter}
                  style={{ alignSelf: 'flex-start', border: '1px solid #fcd34d', background: '#fff', color: '#92400e', borderRadius: 8, padding: '5px 10px', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}
                >
                  Configurar impresora
                </button>
              )}
            </div>
          )}
          {printStatus === 'error' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: '10px 16px', backgroundColor: '#fef2f2', color: '#b91c1c', fontSize: 12 }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                <Icon name="AlertCircle" size={14} color="currentColor" />
                <span>{printErrorMessage || 'No se pudo imprimir el ticket.'} La venta ya quedó registrada.</span>
              </div>
              {onReprint && (
                <button
                  onClick={onReprint}
                  style={{ alignSelf: 'flex-start', border: '1px solid #fca5a5', background: '#fff', color: '#b91c1c', borderRadius: 8, padding: '5px 10px', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}
                >
                  Reintentar impresión
                </button>
              )}
            </div>
          )}

          {showFiscalPilot && (
            <section aria-label="Boleta electrónica SII" style={{ margin: '12px 16px', padding: 12, border: '1px solid #d1d5db', borderRadius: 10, background: '#f9fafb', color: '#374151', fontSize: 12 }}>
              <strong style={{ display: 'block', fontSize: 13, marginBottom: 5 }}>Boleta electrónica · SII</strong>
              <div>{fiscalCheck.status === 'checking' ? 'Verificando venta y autorización…' : fiscalCheck.status === 'error' ? 'No fue posible verificar el estado fiscal. No se emitió ninguna boleta.' : fiscalCheck.status === 'ready' ? 'Emisión pendiente de habilitación. Esta venta tiene solo ticket interno.' : 'Sin verificación fiscal.'}</div>
              {fiscalCheck.status === 'error' && <div style={{ marginTop: 5, fontFamily: 'monospace', fontSize: 11 }}>Diagnóstico: {fiscalCheck.error || 'UNKNOWN_ERROR'}</div>}
              {fiscalCheck.status === 'ready' && <div style={{ marginTop: 4 }}>Venta: {fiscalCheck.data?.salePaid ? 'pagada' : 'no confirmada como pagada'} · Folio SII: {fiscalCheck.data?.fiscalFolio || 'no asignado'}</div>}
              {fiscalCheck.status === 'ready' && <div style={{ marginTop: 5, fontSize: 11, color: '#6b7280' }}>SII Solutions: {fiscalCheck.data?.providerConfigured ? 'clave configurada' : 'falta configurar clave en Preview'} · Emisión: bloqueada hasta validar registro fiscal y protección contra duplicados.</div>}
              {fiscalCheck.status === 'ready' && <div style={{ marginTop: 5, fontSize: 11, color: '#6b7280' }}>Emisor fiscal: {fiscalCheck.data?.issuerRegistered ? 'registrado' : 'no registrado'} · Verificación: {fiscalCheck.data?.issuerVerified ? 'validada' : 'pendiente'} · Estado: {({ pending: 'preparada (sin emitir)', submitting: 'en proceso', submitted: 'enviada al proveedor', accepted: 'aceptada', rejected: 'rechazada', unknown: 'requiere conciliación', cancelled: 'cancelada' })[fiscalCheck.data?.fiscalOperationStatus] || 'sin operación tributaria'}</div>}
              {fiscalCheck.status === 'ready' && fiscalCheck.data?.salePaid && fiscalCheck.data?.issuerRegistered && fiscalCheck.data?.providerConfigured && (
                <div style={{ marginTop: 8 }}>
                  <button type="button" onClick={prepareFiscalOperation} disabled={fiscalPreparation.status === 'working' || fiscalPreparation.status === 'prepared'} style={{ padding: '8px 12px', border: '1px solid #64748b', borderRadius: 7, background: '#fff', color: '#334155', cursor: 'pointer' }}>
                    {fiscalPreparation.status === 'working' ? 'Preparando…' : fiscalPreparation.status === 'prepared' ? 'Operación fiscal preparada' : 'Preparar operación fiscal (sin emitir)'}
                  </button>
                  {fiscalPreparation.status === 'error' && <div style={{ marginTop: 4, color: '#b91c1c' }}>Preparación fallida: {fiscalPreparation.error}</div>}
                  {fiscalPreparation.status === 'prepared' && <div style={{ marginTop: 4 }}>Operación registrada. No se emitió ninguna boleta ni se asignó folio.</div>}
                </div>
              )}
              <div style={{ marginTop: 8, padding: 8, background: '#fff', border: '1px solid #e5e7eb', borderRadius: 7 }}>
                <strong>Diagnóstico tributario de la venta</strong>
                <div>{fiscalPreflight.status === 'checking' ? 'Revisando productos y pagos…' :
                  fiscalPreflight.status === 'error' ? `No disponible: ${fiscalPreflight.error}` :
                  fiscalPreflight.status === 'ready' ? (fiscalPreflight.data?.saleChecksPassed
                    ? 'Venta y pagos conciliados.' : 'Venta o pagos requieren revisión.') : 'Sin diagnóstico.'}</div>
                {fiscalPreflight.status === 'ready' && <div style={{ marginTop: 4 }}>
                  IVA: {fiscalPreflight.data?.taxMappingVerified ? 'todos los productos verificados' : `verificados ${fiscalPreflight.data?.taxVerifiedItemCount ?? 0} de ${fiscalPreflight.data?.taxItemCount ?? '—'}`}
                  {' · '}Emisor: {fiscalPreflight.data?.issuerVerified ? 'verificado' : 'no verificado'}
                  {' · '}Emisión: bloqueada
                </div>}
              </div>
              {fiscalPreflight.status === 'ready' && !fiscalPreflight.data?.taxMappingVerified && (
                <div style={{marginTop:8,padding:8,border:'1px solid #d1d5db',borderRadius:7,background:'#fff'}}>
                  <strong>Verificación manual de IVA</strong>
                  <p style={{margin:'4px 0 8px'}}>Clasifica cada línea únicamente con respaldo tributario. Esto no emite boletas.</p>
                  {items.filter(item=>item?.id).map(item=>(
                    <div key={item.id} style={{marginBottom:10,borderTop:'1px solid #eee',paddingTop:6}}>
                      <div>{item.name||item.description||'Producto'}</div>
                      <select aria-label="Clasificación IVA" value={taxSelection[item.id]||''}
                        onChange={event=>setTaxSelection(previous=>({...previous,[item.id]:event.target.value}))}
                        style={{width:'100%',padding:6,marginTop:4}}>
                        <option value="">Seleccionar tratamiento tributario</option>
                        <option value="IVA_AFECTO_19">Afecto a IVA 19 %</option>
                        <option value="EXENTO">Exento</option>
                      </select>
                      <textarea aria-label="Respaldo tributario" rows={2}
                        placeholder="Fundamento de la clasificación (mínimo 15 caracteres)"
                        value={taxEvidence[item.id]||''}
                        onChange={event=>setTaxEvidence(previous=>({...previous,[item.id]:event.target.value}))}
                        style={{width:'100%',marginTop:5,padding:6,boxSizing:'border-box'}} />
                      <button type="button" onClick={()=>saveTaxClassification(item.id)}
                        disabled={!taxSelection[item.id]||(taxEvidence[item.id]||'').trim().length<15||taxSaving[item.id]==='saving'}
                        style={{padding:'6px 10px',marginTop:4}}>
                        {taxSaving[item.id]==='saving'?'Guardando…':'Registrar clasificación verificada'}
                      </button>
                      {taxSaving[item.id] && taxSaving[item.id]!=='saving' && <div style={{marginTop:4}}>
                        {taxSaving[item.id]==='saved'?'Clasificación registrada (sin emitir).':`No se guardó: ${taxSaving[item.id]}`}
                      </div>}
                    </div>
                  ))}
                </div>
              )}
              <button type="button" disabled style={{ marginTop: 9, padding: '8px 12px', borderRadius: 7, border: '1px solid #d1d5db', color: '#6b7280', background: '#e5e7eb', cursor: 'not-allowed' }}>Emitir boleta electrónica (próximamente)</button>
              <div style={{ marginTop: 6, color: '#6b7280', fontSize: 11 }}>No se envían documentos al SII ni se consumen folios.</div>
            </section>
          )}

          {/* Ticket preview (screen only) */}
          {ticketBody}

          {/* Action buttons */}
          <div style={{
            display: 'flex', gap: 10, padding: '12px 16px 16px',
            borderTop: '1px solid #f0f0f0',
          }}>
            <button
              onClick={onReprint}
              style={{
                flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
                gap: 6, padding: '10px 0', borderRadius: 10, border: '1.5px solid #d1d5db',
                backgroundColor: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 600, color: '#374151',
              }}
            >
              <Icon name="Printer" size={15} color="currentColor" />
              Reimprimir
            </button>
            <button
              onClick={onNewSale}
              style={{
                flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
                gap: 6, padding: '10px 0', borderRadius: 10, border: 'none',
                backgroundColor: '#059669', cursor: 'pointer', fontSize: 13, fontWeight: 700, color: '#fff',
              }}
            >
              <Icon name="Plus" size={15} color="currentColor" />
              Nueva venta
            </button>
          </div>
        </div>
      </div>

      {/* Print-only portal — mounted directly in document.body so the CSS rule
          `body > *:not(#crm-thermal-print-root) { display: none }` can hide
          the entire app (#root) while showing only this ticket.
          position: static + @page size: 58mm auto = exactly 1 page.            */}
      {ReactDOM.createPortal(
        <div id="crm-thermal-print-root" aria-hidden="true" style={{ display: 'none' }}>
          {ticketBody}
        </div>,
        document.body
      )}
    </>
  );
}
